import classes from "./MemberPage.module.css";
import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import memberImage from "../../public/fakeUser/noUser.webp";
import Overview from "../../components/MemberPage/Overview";
import Arbeiten from "../../components/MemberPage/Arbeiten";
import Services from "../../components/MemberPage/Services";
import Bewertungen from "../../components/MemberPage/Bewertungen";
import { useCreativeMemberPage } from "../../hooks/creative/index";
import ProjectOverview from "../CreativeEditingPage/ProjectOverview";
import { NavBarContext } from "../../App";
import { useDropdown } from "../../hooks/useDropdown";
import {
  buildBookingAuthNavigationState,
  buildStudioBookingRedirectLocation,
  storeStudioBookingRedirect,
} from "../../util/bookingAuthRedirect";
import { createPersistableBookingData } from "../../store/bookingStore";
import {
  formatCreativeExperience,
  formatCreativeRating,
  formatCreativeBookings,
  hasCreativeRating,
} from "../../util/creativeStats";

export const MemberPage = () => {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const location = useLocation();

  const { creative, loading, error, isNotFound } = useCreativeMemberPage(id);

  // Get current user from Redux
  const { user: currentUser, isAuthenticated } = useSelector((state) => state.auth || {});
  const currentUserId = currentUser?._id || currentUser?.id;
  
  // Check if current user is the owner of the creative
  const isOwner = currentUserId && creative?.userID && currentUserId === creative.userID;

  // Redirect to 404 if creative is not found (not published or doesn't exist)
  useEffect(() => {
    if (!loading && isNotFound) {
      navigate('/error/404', { replace: true });
    }
  }, [loading, isNotFound, navigate]);
  
  // Check if we're on a project overview page
  const isProjectOverview = location.pathname.includes('/project/') && location.pathname.includes('/overview');

  const { isOpen: showDropdown, toggle: toggleDropdown, triggerRef, dropdownRef } = useDropdown();
  const [activeTab, setActiveTab] = useState('overview');
  const containerLeftRef = useRef(null);
  const namePhoneRef = useRef(null);
  const phoneNavRef = useRef(null);
  const [isPhoneNavFixed, setIsPhoneNavFixed] = useState(false);
  const mainWrapperRef = useRef(null);
  const stickSentinelRef = useRef(null);
  const unstickSentinelRef = useRef(null);
  const [passedStick, setPassedStick] = useState(false);
  const [hitUnstick, setHitUnstick] = useState(false);
  
  // Reset scroll position and observer states when component mounts or returns from project overview
  useEffect(() => {
    // Skip if on project overview page
    if (isProjectOverview) return;
    
    // Reset scroll to top when returning to MemberPage
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    // Reset intersection observer states
    setPassedStick(false);
    setHitUnstick(false);
    setIsPhoneNavFixed(false);
  }, [id, isProjectOverview]);

  const handleTabClick = (tabKey) => {
    if (tabKey !== activeTab) {
      setActiveTab(tabKey);
      navigate(`/members/${id}/${tabKey}`, { replace: true, state: location.state });
    }
  };

  const handleBackClick = () => {
    // Pop member off the stack so studio back still reaches search (not member again).
    // Tab switches use replace, so -1 won't stop on intermediate member tabs.
    if (location.state?.fromStudioId) {
      navigate(-1);
      return;
    }

    const studioId = creative?.studios?.items[0]?.studioID;
    if (studioId) {
      navigate(`/studio/${studioId}`);
    } else {
      navigate(-1);
    }
  };

  const { isPhoneSize } = useContext(NavBarContext);

  const handleMitbuchenClick = () => {
    const studioId = location.state?.fromStudioId || creative?.studios?.items[0]?.studioID;
    
    if (!studioId) return;

    // Set addFreelancer to true in booking data
    // Note: We need to update the booking store for the specific studio
    // Since we're navigating to the studio page, the BookingProvider there will handle it
    // But we can pre-set it in localStorage to ensure it's there when we arrive
    const storageKey = `studioBookingData_${studioId}`;
    const savedData = localStorage.getItem(storageKey);
    let bookingData = {
      studioId,
      date: '',
      startTime: '',
      endTime: '',
      addFreelancer: true,
      capacity: '',
      equipment: [],
      totalStudioPrice: 0,
      totalCreativePrice: 0,
      totalPrice: 0,
      projectName: '',
      companyName: '',
      description: '',
      creativePackages: [],
    };

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.data && Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          bookingData = { ...parsed.data, addFreelancer: true };
        }
      } catch (e) {
        // Use default bookingData
      }
    }

    // Save to localStorage
    localStorage.setItem(storageKey, JSON.stringify({
      data: createPersistableBookingData(bookingData),
      timestamp: Date.now()
    }));

    const bookingTarget = buildStudioBookingRedirectLocation({ studioId, isPhoneSize });

    if (!isAuthenticated && !currentUserId) {
      storeStudioBookingRedirect({ studioId, isPhoneSize });
      const state = buildBookingAuthNavigationState({ location, from: bookingTarget });
      navigate(isPhoneSize ? '/anmelden' : '/', { state });
      return;
    }

    navigate(bookingTarget);
  };

  const TABS = {
    overview: { label: 'Übersicht', component: Overview },
    arbeiten: { label: 'Arbeiten', component: Arbeiten },
    services: { label: 'Services', component: Services },
    bewertungen: { label: 'Bewertungen', component: Bewertungen },
  };

  const ActiveComponent = TABS[activeTab]?.component || TABS.overview.component;

  // Sync active tab with URL and ensure base URL redirects to /overview
  useEffect(() => {
    // Skip tab sync if we're on project overview page
    if (isProjectOverview) return;
    
    const currentTab = tab || 'overview';

    if (!TABS[currentTab]) {
      // Invalid or missing tab: normalize to /overview
      navigate(`/members/${id}/overview`, { replace: true, state: location.state });
      setActiveTab('overview');
      return;
    }

    setActiveTab(currentTab);
  }, [tab, id, navigate, location.pathname, isProjectOverview]);

  useEffect(() => {
    if (isPhoneSize) {
      if (mainWrapperRef.current) {
        const headerHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--headerPhoneHeight') || '53',
          10
        );
        const navHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--navPhoneHeight') || '52',
          10
        );
        const lineHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--thickLineHeight') || '4',
          10
        );
        const rect = mainWrapperRef.current.getBoundingClientRect();
        const absoluteTop =
          rect.top + window.pageYOffset - headerHeight - navHeight - lineHeight;
        window.scrollTo({ top: Math.max(0, absoluteTop), behavior: 'auto' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      return;
    }

    if (containerLeftRef.current) {
      const rect = containerLeftRef.current.getBoundingClientRect();
      const absoluteTop = rect.top + window.pageYOffset - 80;
      window.scrollTo({ top: absoluteTop, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeTab, isPhoneSize]);


  useEffect(() => {
    // Skip if on project overview page
    if (isProjectOverview) return;
    
    // Reset states before initializing observers to ensure clean state
    setPassedStick(false);
    setHitUnstick(false);
    
    // Sentinel-based fixing/unfixing for phone navigation
    const getPxVar = (name, fallback) => {
      const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      const parsed = parseInt(val, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const headerHeight = getPxVar('--headerPhoneHeight', 53);
    const navHeight = getPxVar('--navPhoneHeight', 56);
    const lineHeight = getPxVar('--thickLineHeight', 4);

    let stickObserver = null;
    let unstickObserver = null;

    // Small delay to ensure DOM is fully settled before observing
    const timeoutId = setTimeout(() => {
      stickObserver = new IntersectionObserver(
        ([entry]) => {
          // If sentinel above nav leaves viewport (not intersecting), nav reached header bottom
          setPassedStick(!entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: `-${headerHeight}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      unstickObserver = new IntersectionObserver(
        ([entry]) => {
          // If main top intersects at header+nav+line threshold, unstick
          setHitUnstick(entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: `-${headerHeight + navHeight + lineHeight}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      if (stickSentinelRef.current) stickObserver.observe(stickSentinelRef.current);
      if (unstickSentinelRef.current) unstickObserver.observe(unstickSentinelRef.current);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (stickObserver) stickObserver.disconnect();
      if (unstickObserver) unstickObserver.disconnect();
    };
  }, [id, isProjectOverview]);

  useEffect(() => {
    setIsPhoneNavFixed(passedStick && !hitUnstick);
  }, [passedStick, hitUnstick]);

  // If it's the project overview page, render it as a standalone page
  if (isProjectOverview) {
    return <ProjectOverview creative={creative} loading={loading} error={error} />;
  }

  return (
      <div ref={containerLeftRef} className={classes.memberPage}>
        <header className={classes.header}>
            <div className={classes.headerContainer}>
                <button className={classes.backButton} onClick={handleBackClick}>
                    <div className={classes.backButtonArrow}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M0 7.90197L7.90197 0L8.95756 1.05559L2.07028 7.94287L9 15.4501L7.90306 16.4626L0.0119278 7.9139L0 7.90197Z" fill="white"/>
                        </svg>
                    </div>
                    <span className="mediumLarge">Zurück zum Studio</span>
                </button>
                <nav className={classes.navigation}>
                    {Object.entries(TABS).map(([key, tab]) => (
                        <button
                            key={key}
                            className={`${classes.navButton} semiboldLarge ${activeTab === key ? classes.active : ''}`}
                            onClick={() => handleTabClick(key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>
        </header>
        <header className={classes.headerPhone}>
            <div className={classes.headerTopContainer}> 
                <button onClick={handleBackClick} className={classes.backButton}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 7.90197L7.90197 0L8.95756 1.05559L2.07028 7.94287L9 15.4501L7.90306 16.4626L0.0119278 7.9139L0 7.90197Z" fill="black"/>
                    </svg>
                <span className="mediumLarge">{creative?.displayName || ''}</span>
                </button>
                {/* <button className={`${classes.editButton} mediumLarge`}>Bearbeiten</button> */}
            </div>
        </header>
        <div className={classes.profilePhoneContainer}>
            <div className={classes.profileContainer}>
                <div className={classes.memberImageWrapper}>
                    <div className={classes.memberImageContainer}>
                        <img src={creative?.processedProfilePicture || memberImage} alt="member" />
                    </div>
                    <div className={classes.checkBoxContainer}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                            <path d="M12.3326 0C5.52145 0 0 5.52145 0 12.3326C0 19.1438 5.52145 24.6653 12.3326 24.6653C19.1438 24.6653 24.6653 19.1438 24.6653 12.3326C24.6653 5.52145 19.1438 0 12.3326 0ZM18.6857 10.0801C17.7491 11.1835 16.7874 12.2655 15.835 13.3558C14.9121 14.4131 13.9881 15.4698 13.063 16.5255C12.9423 16.6635 12.8196 16.7994 12.69 16.929C12.0907 17.5272 11.1909 17.609 10.5219 17.0838C9.12249 15.9851 7.72888 14.8785 6.35416 13.7493C5.45534 13.0111 5.4375 11.8819 6.27021 11.141C6.56561 10.8776 6.91717 10.7748 7.32224 10.7858C7.7782 10.7664 8.17331 10.9521 8.53063 11.2381C9.31401 11.8646 10.0969 12.4921 10.8808 13.1171C11.2103 13.3799 11.4113 13.359 11.6862 13.0447C13.0929 11.437 14.4986 9.82875 15.908 8.22315C16.0969 8.00698 16.2826 7.78345 16.5339 7.63339C17.2407 7.21152 18.0954 7.36159 18.6627 8.00593C19.1622 8.57261 19.1931 9.48192 18.6857 10.0801Z" fill="#DF315A"/>
                        </svg>
                    </div>
                </div>
                <div className={classes.stats}>
                    <div className={classes.stat}>
                        <span className={`${classes.statValue} semiboldMiddle`}>{formatCreativeExperience(creative)}</span>
                        <span className="regularXxs">Erfahrung</span>
                    </div>
                    <div className={classes.stat}>
                        <span className={`${classes.statValue} semiboldMiddle`}>
                            {formatCreativeRating(creative)}
                            {hasCreativeRating(creative) && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
                                    <g clipPath="url(#clip0_4470_12135)">
                                    <path d="M8.80204 8.12174L9.24303 10.7064L5.91724 8.96697L8.61217 7.02539L8.74079 7.7665L8.80204 8.12174Z" fill="#1D1E1B"/>
                                    <path d="M3.09436 7.76638L3.22299 7.02527L0.53418 4.42221L4.25196 3.88323L5.91792 0.539062L7.58387 3.88323L11.3017 4.42221L2.59213 10.7063L3.09436 7.76638Z" fill="#1D1E1B"/>
                                    </g>
                                    <defs>
                                    <clipPath id="clip0_4470_12135">
                                    <rect width="10.7675" height="10.1672" fill="white" transform="translate(0.53418 0.539062)"/>
                                    </clipPath>
                                    </defs>
                                </svg>
                            )}
                        </span>
                        <span className="regularXxs">Sterne</span>
                    </div>
                    <div className={classes.stat}>
                        <span className={`${classes.statValue} semiboldMiddle`}>{formatCreativeBookings(creative)}</span>
                        <span className="regularXxs">Buchungen</span>
                    </div>
                </div>
            </div>
            <div className={classes.memberNameContainerPhone} ref={namePhoneRef}>
                <h1 className="semiboldMiddle">{creative?.displayName || ''}</h1>
                <p className="regularXxs">{creative?.jobTitle || ''}</p>
            </div>
        </div>
        {/* Stick sentinel: sits right above the phone nav container */}
        <div ref={stickSentinelRef} style={{ position: 'absolute', inset: 'auto auto auto auto' }}></div>
        <div ref={phoneNavRef} className={`${classes.phoneNavigationContainer} ${isPhoneNavFixed ? classes.phoneNavigationFixed : ''}`}>
            <nav className={classes.navigationPhone}>
                {Object.entries(TABS).map(([key, tab]) => (
                    <button key={key} className={`${classes.navButtonPhone} semiboldSmall ${activeTab === key ? classes.activePhone : ''}`} onClick={() => handleTabClick(key)}>
                        {tab.label}
                    </button>
                ))}
            </nav>
            <div className={classes.thickLine}></div>    
        </div>
        {isPhoneNavFixed && (
          <div className={classes.phoneNavigationSpacer}></div>
        )}
        {/* Unstick sentinel: placed at main content top */}
        <div ref={unstickSentinelRef} style={{ height: 0, width: 0 }}></div>
        <div ref={mainWrapperRef} className={classes.mainWrapper}>
            <div className={classes.main}>
                <div className={classes.containerRight}>
                    <div className={classes.memberContainer}>
                        <div className={classes.memberImageWrapper}>
                            <div className={classes.memberImageContainer}>
                                <img src={ creative?.processedProfilePicture || memberImage} alt="member" />
                            </div>
                            <div className={classes.checkBoxContainer}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                    <path d="M12.3326 0C5.52145 0 0 5.52145 0 12.3326C0 19.1438 5.52145 24.6653 12.3326 24.6653C19.1438 24.6653 24.6653 19.1438 24.6653 12.3326C24.6653 5.52145 19.1438 0 12.3326 0ZM18.6857 10.0801C17.7491 11.1835 16.7874 12.2655 15.835 13.3558C14.9121 14.4131 13.9881 15.4698 13.063 16.5255C12.9423 16.6635 12.8196 16.7994 12.69 16.929C12.0907 17.5272 11.1909 17.609 10.5219 17.0838C9.12249 15.9851 7.72888 14.8785 6.35416 13.7493C5.45534 13.0111 5.4375 11.8819 6.27021 11.141C6.56561 10.8776 6.91717 10.7748 7.32224 10.7858C7.7782 10.7664 8.17331 10.9521 8.53063 11.2381C9.31401 11.8646 10.0969 12.4921 10.8808 13.1171C11.2103 13.3799 11.4113 13.359 11.6862 13.0447C13.0929 11.437 14.4986 9.82875 15.908 8.22315C16.0969 8.00698 16.2826 7.78345 16.5339 7.63339C17.2407 7.21152 18.0954 7.36159 18.6627 8.00593C19.1622 8.57261 19.1931 9.48192 18.6857 10.0801Z" fill="#DF315A"/>
                                </svg>
                            </div>
                        </div>
                        <div className={classes.memberNameContainer}>
                            <h2 className="titleSemibold2">{creative?.displayName || ''}</h2>
                            <p className="regularMiddle">{creative?.jobTitle || ''}</p>
                        </div>
                    </div>
                    <div className={classes.horizontalLine}></div>
                    <div className={classes.infosPayment}>
                        <div className={classes.moneyContainer}>
                            <span className="titleSemibold3">ab 85 €</span>
                            <span className="regularLarge">pro Stunde</span>
                        </div>
                        {/* <span 
                            style={{color: "var(--dark-grey-4)"}} 
                            className="regularXxs"
                        >
                            Mind. 3 Stunden
                        </span> */}
                    </div>
                    <div className={classes.horizontalLine}></div>
                    <div className={classes.servicesContainer}>
                        <h3 className="regularSmall">Skills & Services</h3>
                        <div className={classes.servicesContent}>
                            {creative?.skills?.slice(0, 4).map((skill, index) => (
                                <div className={classes.serviceItem} key={index}>
                                    <span className="regularMiddle">{skill}</span>
                                </div>
                            ))}
                            {creative?.skills?.length > 4 && (
                                <button ref={triggerRef} onClick={toggleDropdown} className={`${classes.allViewButton} mediumLarge`}>+{creative?.skills?.length - 4}</button>
                            )}
                            {showDropdown && (
                                <div ref={dropdownRef} className={classes.dropdownContainer}>
                                    {creative?.skills?.slice(4).map((skill, index) => (
                                        <div className={classes.serviceItem} key={index}>
                                            <span className="regularMiddle">{skill}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {!isOwner && (
                      <div className={classes.horizontalLine}></div>
                    )}

                    {!isOwner && (
                      <button className={classes.bookButton} onClick={handleMitbuchenClick}>
                          <span className="semiboldMedium">Mitbuchen</span>
                      </button>
                    )}
                </div>
                <div className={classes.containerLeft}>
                    <div className={classes.tabContentFade} key={activeTab}>
                        <ActiveComponent
                          creative={creative}
                          loading={loading}
                          error={error}
                          setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
            </div>
        </div>
        {!isOwner && (
          <footer className={classes.footerPhone}>
            <span className={`${classes.buttonText} regularLarge`}>
              <span className={classes.priceAmount}>ab 40 €</span>
              <span className={classes.priceUnit}> pro Stunde</span>
            </span>
            <button className={`${classes.mitbuchButton} regularLarge`} onClick={handleMitbuchenClick}>Mitbuchen</button>
          </footer>
        )}
      </div>
  );
};

export default MemberPage;
